import React from 'react'
import {
  Box,
  Divider,
  Flex,
  Grid,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  Button,
  VStack,
} from '@chakra-ui/react'
import FastAvatar from '../image/FastAvatar'
import LazyLoadImg from '../image/LazyLoadImg'
import { useTranslation } from 'react-i18next'
import { CircleFlag } from 'react-circle-flags'
import { BriefcaseBusiness, Mail, MapPin, Phone, UserRound, UsersRound } from 'lucide-react'
import { getCountryNameByCode } from '../countries/PhoneDial'

interface OrganizationModalCardProps {
  person: {
    first_name?: string
    last_name?: string
    profile_picture?: string
    current_position?: {
      title?: string
      job_profile_accountability_areas?: {
        id: string
        accountability_area: { id: string; title: string }
        raci_labels: string[]
      }[]
    }
    country_of_residence?: string
    country_of_work?: string
    work_email?: string
    phone_number?: string
    teams?: { id: string; name: string }[]
    primary_evaluator?: {
      first_name?: string
      last_name?: string
      profile_picture?: string
    }
  } | null
  isOpen: boolean
  onClose: () => void
}

const DetailItem = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) => (
  <Box
    p="16px"
    borderRadius="14px"
    bg="white"
    border="1px solid"
    borderColor="solid.50"
    minH="112px"
  >
    <HStack align="flex-start" spacing="12px">
      <Flex
        w="34px"
        h="34px"
        borderRadius="10px"
        bg="primary.0"
        color="primary.200"
        align="center"
        justify="center"
        flexShrink={0}
      >
        {icon}
      </Flex>
      <Box minW={0} flex="1">
        <HStack spacing="8px" align="center" minH="34px">
          <Text fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.6px">
            {label}
          </Text>
        </HStack>
        <Box mt="8px">{children}</Box>
      </Box>
    </HStack>
  </Box>
)

const OrganizationModalCard: React.FC<OrganizationModalCardProps> = ({ person, isOpen, onClose }) => {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered>
      <ModalOverlay bg="rgba(15, 23, 42, 0.42)" backdropFilter="blur(6px)" />
      <ModalContent
        borderRadius="24px"
        overflow="hidden"
        bg="white"
        boxShadow="0 24px 80px rgba(15, 23, 42, 0.18)"
      >
        <ModalCloseButton
          zIndex={2}
          top="18px"
          right="18px"
          border="1px solid"
          borderColor="solid.100"
          borderRadius="full"
          bg="white"
        />
        <ModalBody p={0}>
          {person && (
            <Flex direction={{ base: 'column', lg: 'row' }} minH={{ base: 'auto', lg: '560px' }}>
              <Flex
                position="relative"
                flexBasis={{ base: '100%', lg: '38%' }}
                minH={{ base: '300px', lg: '560px' }}
                bg="linear-gradient(160deg, var(--chakra-colors-primary-0) 0%, #f7f4ec 100%)"
                overflow="hidden"
              >
                {person.profile_picture ? (
                  <LazyLoadImg
                    src={person.profile_picture}
                    alt={`${person.first_name || ''} ${person.last_name || ''}`}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                ) : (
                  <Flex
                    w="100%"
                    h="100%"
                    bg="solid.200"
                    color="solid.700"
                    align="center"
                    justify="center"
                  >
                    <Text fontSize="4.25rem" fontWeight={700}>
                      {`${person.first_name || ''} ${person.last_name || ''}`.split(' ').map((n) => n[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()}
                    </Text>
                  </Flex>
                )}
              </Flex>

              <Box
                flex="1"
                bg="white"
                p={{ base: 6, md: 8, lg: 10 }}
                display="flex"
                alignItems={{ base: 'stretch', lg: 'center' }}
              >
                <VStack
                  align="stretch"
                  spacing={6}
                  w="100%"
                  maxW="640px"
                  py={{ base: 0, lg: 8 }}
                >
                  <Box pt={{ base: 0, lg: 4 }}>
                    <Text fontSize={{ base: '28px', md: '34px' }} lineHeight="1.05" fontWeight="700" color="solid.900">
                      {person.first_name || ''} {person.last_name || ''}
                    </Text>
                    <Text color="solid.600" fontSize="15px" mt="8px">
                      {person.current_position?.title || t('common.labels.unknownPosition')}
                    </Text>
                    {!!person.teams?.length && (
                      <HStack mt="12px" spacing="8px" flexWrap="wrap">
                        {person.teams.map((team) => (
                          <HStack key={team.id} spacing="6px" px="10px" py="5px" borderRadius="full" bg="primary.0" color="primary.200">
                            <UsersRound size={13} />
                            <Text fontSize="12px" fontWeight="600">{team.name}</Text>
                          </HStack>
                        ))}
                      </HStack>
                    )}
                  </Box>

                  <Divider borderColor="solid.50" />

                  <Grid
                    templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                    gap={4}
                    pt={{ base: 0, lg: 2 }}
                  >
                    <DetailItem icon={<BriefcaseBusiness size={18} />} label={t('common.fields.countryOfWork')}>
                      {person.country_of_work ? (
                        <HStack spacing="10px">
                          <CircleFlag countryCode={person.country_of_work.toLowerCase()} style={{ width: 22, height: 22 }} />
                          <Text fontSize="14px" color="solid.800" fontWeight="600">
                            {getCountryNameByCode(person.country_of_work) || person.country_of_work}
                          </Text>
                        </HStack>
                      ) : (
                        <Text fontSize="14px" color="solid.500">
                          {t('common.labels.notSpecified')}
                        </Text>
                      )}
                    </DetailItem>

                    <DetailItem icon={<MapPin size={18} />} label={t('common.fields.countryOfResidence')}>
                      {person.country_of_residence ? (
                        <HStack spacing="10px">
                          <CircleFlag countryCode={person.country_of_residence.toLowerCase()} style={{ width: 22, height: 22 }} />
                          <Text fontSize="14px" color="solid.800" fontWeight="600">
                            {getCountryNameByCode(person.country_of_residence) || person.country_of_residence}
                          </Text>
                        </HStack>
                      ) : (
                        <Text fontSize="14px" color="solid.500">
                          {t('common.labels.notSpecified')}
                        </Text>
                      )}
                    </DetailItem>

                    <DetailItem icon={<Mail size={18} />} label={t('common.fields.email')}>
                      <Text fontSize="14px" color="solid.800" fontWeight="600" wordBreak="break-word">
                        {person.work_email || t('common.labels.na')}
                      </Text>
                    </DetailItem>

                    <DetailItem icon={<Phone size={18} />} label="Phone">
                      <Text fontSize="14px" color="solid.800" fontWeight="600">
                        {person.phone_number || t('common.labels.notSpecified')}
                      </Text>
                    </DetailItem>

                    <DetailItem icon={<UserRound size={18} />} label={t('common.fields.manager')}>
                      {person.primary_evaluator ? (
                        <HStack spacing="10px" align="center">
                          <FastAvatar
                            src={person.primary_evaluator.profile_picture || ''}
                            name={`${person.primary_evaluator.first_name || ''} ${person.primary_evaluator.last_name || ''}`}
                            size="sm"
                          />
                          <Box minW={0}>
                            <Text fontSize="14px" color="solid.800" fontWeight="600">
                              {person.primary_evaluator.first_name} {person.primary_evaluator.last_name}
                            </Text>
                          </Box>
                        </HStack>
                      ) : (
                        <Text fontSize="14px" color="solid.500">
                          {t('common.labels.notSpecified')}
                        </Text>
                      )}
                    </DetailItem>
                  </Grid>

                  {!!person.current_position?.job_profile_accountability_areas?.length && (
                    <Box p="16px" borderRadius="14px" bg="solid.0" border="1px solid" borderColor="solid.50">
                      <HStack spacing="10px" mb="12px">
                        <Flex w="32px" h="32px" borderRadius="10px" bg="primary.0" color="primary.200" align="center" justify="center">
                          <BriefcaseBusiness size={17} />
                        </Flex>
                        <Box>
                          <Text fontSize="13px" fontWeight="700" color="solid.900">Responsibilities</Text>
                          <Text fontSize="11px" color="solid.500">Areas this colleague can help with</Text>
                        </Box>
                      </HStack>
                      <HStack spacing="8px" flexWrap="wrap">
                        {person.current_position.job_profile_accountability_areas.map((area) => (
                          <Box key={area.id} px="10px" py="7px" borderRadius="10px" bg="white" border="1px solid" borderColor="solid.50">
                            <Text fontSize="12px" fontWeight="600" color="solid.800">{area.accountability_area.title}</Text>
                            {!!area.raci_labels.length && (
                              <Text fontSize="10px" color="solid.400" textTransform="capitalize" mt="1px">
                                {area.raci_labels.join(' · ')}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </HStack>
                    </Box>
                  )}

                  {(person.work_email || person.phone_number) && (
                    <HStack spacing="10px" pt="4px">
                      {person.work_email && (
                        <Button as="a" href={`mailto:${person.work_email}`} leftIcon={<Mail size={16} />} variant="primary" size="sm">
                          Send email
                        </Button>
                      )}
                      {person.phone_number && (
                        <Button as="a" href={`tel:${person.phone_number}`} leftIcon={<Phone size={16} />} variant="white" size="sm">
                          Call
                        </Button>
                      )}
                    </HStack>
                  )}
                </VStack>
              </Box>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default OrganizationModalCard
